import * as z from 'zod'

// Price object schema
export const priceSchema = z.object({
  currency: z.string(),
  amount: z.number(),
  amount_minor: z.number(),
})

// Individual calendar day data for availability endpoint
export const availabilityDaySchema = z.object({
  // Availability fields
  bookable_departures: z.array(z.string()),

  // Minimum stay requirements
  min_days: z.number(),
  min_days_violated: z.boolean(),

  // Pricing fields (for minimum stay)
  price: priceSchema,
  price_fx: z.record(z.number()),
  nights: z.number(),
  pitches_to_sell: z.number(),

  // Tax information
  price_includes_taxes: z.boolean(),
  has_arrival_taxes: z.boolean(),
})

// Legacy unified calendar day schema (simplified for backward compatibility)
export const unifiedCalendarDaySchema = z.object({
  // Availability fields
  bookable_departures: z.array(z.string()),

  // Minimum stay requirements
  min_days: z.number().optional(),
  min_days_violated: z.boolean().optional(),

  // Pricing fields (optional, only present when nights parameter is provided)
  price: priceSchema.optional(),
  price_fx: z.record(z.number()).optional(),
  nights: z.number().optional(),
  pitches_to_sell: z.number().optional(),
})

// Window metadata
export const windowSchema = z.object({
  start: z.string(),
  end: z.string(),
})

// Meta information for availability endpoint
export const availabilityMetaSchema = z.object({
  start: z.string(),
  end: z.string(),
  nights: z.number(),
  type: z.literal('availability'),
  price_includes_taxes: z.boolean(),
  show_arrival_tax_indicator: z.boolean(),
})

// Pricing endpoint meta schema
export const pricingMetaSchema = z.object({
  start: z.string(),
  end: z.string(),
  nights: z.number(),
  type: z.literal('duration_pricing'),
})

// Legacy meta schema (kept for backward compatibility)
export const metaSchema = z.object({
  start: z.string(),
  end: z.string(),
  nights: z.number().nullable().optional(),
  currency: z.string().optional(),
})

// Calendar API v2 - Availability endpoint response (new structure)
export const availabilityCalendarResponseSchema = z.object({
  pitchtype_id: z.number(),
  window: windowSchema,
  calendar_data: z.record(availabilityDaySchema),
  meta: availabilityMetaSchema,
})

// Calendar API v2 - Pricing endpoint response
export const pricingStaySchema = z.object({
  arrive_date: z.string(),
  price: priceSchema,
  price_fx: z.record(z.number()),
  nights: z.number(),
})

export const pricingCalendarResponseSchema = z.object({
  pitchtype_id: z.number(),
  window: windowSchema,
  pricing_data: z.array(pricingStaySchema),
  meta: pricingMetaSchema,
})

// Main unified calendar response (legacy - still supported)
export const unifiedCalendarResponseSchema = z.object({
  pitchtype_id: z.number(),
  window: windowSchema,
  calendar_data: z.record(unifiedCalendarDaySchema),
  calendar: z.record(unifiedCalendarDaySchema).optional(), // Backward compatibility
  meta: metaSchema,
})

// Request parameters schema (matches exact API expectations)
export const unifiedCalendarParamsSchema = z.object({
  pitchtype_id: z.string(), // Required
  start: z.string().optional(), // YYYY-MM-DD format
  end: z.string().optional(), // YYYY-MM-DD format
  adults: z.number().optional(), // Default: 2
  child_ages: z.string().optional(), // Comma-separated ages, default: empty
  dogs: z.number().optional(), // Default: 0
  facet: z.array(z.string()).optional(), // Can be specified multiple times for extras
  nights: z.number().optional(), // Required for pricing endpoint only
})

// Component-specific parameters (not sent to API, used internally)
export const componentCalendarParamsSchema = unifiedCalendarParamsSchema.extend(
  {
    slug: z.string().optional(),
    categories: z.array(z.string()).optional(),
  },
)

// Exported types
export type AvailabilityDay = z.infer<typeof availabilityDaySchema>
export type UnifiedCalendarDay = z.infer<typeof unifiedCalendarDaySchema>
export type UnifiedCalendarResponse = z.infer<
  typeof unifiedCalendarResponseSchema
>
export type AvailabilityCalendarResponse = z.infer<
  typeof availabilityCalendarResponseSchema
>
export type PricingCalendarResponse = z.infer<
  typeof pricingCalendarResponseSchema
>
export type PricingStay = z.infer<typeof pricingStaySchema>
export type UnifiedCalendarParams = z.infer<typeof unifiedCalendarParamsSchema>
export type ComponentCalendarParams = z.infer<
  typeof componentCalendarParamsSchema
>
export type Price = z.infer<typeof priceSchema>
export type Window = z.infer<typeof windowSchema>
export type Meta = z.infer<typeof metaSchema>
export type AvailabilityMeta = z.infer<typeof availabilityMetaSchema>
